import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Package, Clock, AlertCircle } from 'lucide-react';
import { mockShipmentStats, mockRecentShipments, mockMonthlyData, mockCourierComparison } from '../mock';

const DashboardMockup = () => {
  const maxShipments = Math.max(...mockMonthlyData.map(d => d.shipments));

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="dashboard-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Totale Spedizioni</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--brand-primary)' }}>{mockShipmentStats.total.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Package className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <TrendingUp className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
              <span style={{ color: 'var(--accent-primary)' }}>+12.5%</span>
              <span style={{ color: 'var(--text-muted)' }}>vs mese scorso</span>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>In Transito</p>
                <p className="text-3xl font-bold mt-2" style={{ color: '#3B82F6' }}>{mockShipmentStats.inTransit.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                <Clock className="w-6 h-6" style={{ color: '#3B82F6' }} />
              </div>
            </div>
            <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              {((mockShipmentStats.inTransit / mockShipmentStats.total) * 100).toFixed(1)}% del totale
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Consegnate</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--accent-primary)' }}>{mockShipmentStats.delivered.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Package className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
              </div>
            </div>
            <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              {((mockShipmentStats.delivered / mockShipmentStats.total) * 100).toFixed(1)}% tasso di successo
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Ritardi</p>
                <p className="text-3xl font-bold mt-2" style={{ color: '#EF4444' }}>{mockShipmentStats.delayed}</p>
              </div>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <AlertCircle className="w-6 h-6" style={{ color: '#EF4444' }} />
              </div>
            </div>
            <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              {((mockShipmentStats.delayed / mockShipmentStats.total) * 100).toFixed(1)}% del totale
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Shipments Chart */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle style={{ color: 'var(--text-primary)' }}>Spedizioni Mensili</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockMonthlyData.map((item, index) => {
                const percentage = (item.shipments / maxShipments) * 100;
                return (
                  <div key={index}>
                    <div className="flex justify-between mb-1 text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>{item.month}</span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{item.shipments}</span>
                    </div>
                    <div className="w-full h-3 rounded-full" style={{ background: 'var(--bg-section)' }}>
                      <div 
                        className="h-full rounded-full transition-all duration-500" 
                        style={{ 
                          width: `${percentage}%`,
                          background: 'linear-gradient(to right, var(--accent-primary), var(--accent-strong))'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Courier Comparison Table */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle style={{ color: 'var(--text-primary)' }}>Confronto Corrieri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Corriere</th>
                    <th className="text-right py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Prezzo</th>
                    <th className="text-right py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  {mockCourierComparison.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-opacity-50" style={{ borderColor: 'var(--border-light)' }}>
                      <td className="py-3 px-2">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{item.courier}</span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="font-semibold" style={{ color: 'var(--accent-primary)' }}>€{item.price.toFixed(2)}</span>
                      </td>
                      <td className="text-right py-3 px-2">
                        <span className="text-sm px-2 py-1 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                          {item.time}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Shipments Table */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle style={{ color: 'var(--text-primary)' }}>Spedizioni Recenti</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                  <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>ID Spedizione</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Destinazione</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Corriere</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Stato</th>
                  <th className="text-left py-3 px-2 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Data</th>
                </tr>
              </thead>
              <tbody>
                {mockRecentShipments.map((shipment, index) => (
                  <tr key={index} className="border-b hover:bg-opacity-50" style={{ borderColor: 'var(--border-light)' }}>
                    <td className="py-3 px-2">
                      <span className="font-mono text-sm" style={{ color: 'var(--text-primary)' }}>{shipment.id}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span style={{ color: 'var(--text-body)' }}>{shipment.destination}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="font-medium" style={{ color: 'var(--text-body)' }}>{shipment.courier}</span>
                    </td>
                    <td className="py-3 px-2">
                      <span 
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          background: shipment.status === 'Consegnato' ? 'var(--accent-wash)' : 
                                      shipment.status === 'In transito' ? 'rgba(59, 130, 246, 0.1)' : 
                                      'rgba(251, 191, 36, 0.1)',
                          color: shipment.status === 'Consegnato' ? 'var(--accent-text)' : 
                                 shipment.status === 'In transito' ? '#3B82F6' : 
                                 '#F59E0B'
                        }}
                      >
                        {shipment.status}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{shipment.date}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardMockup;